import { ExternalLink } from '@components/Link';
import { Typography } from '@components/Typography';
import { MENU_INDUSTRIES } from '@constants/account';
import { RoutePath } from '@constants/routes';
import LogoutModal from '@features/LogoutModal';
import Notifications from '@features/Notifications';
import { getHeaderTitleKey } from '@helpers/headerTitleKey';
import { usePermissions, useTranslation, useVisibilityStatus } from '@hooks';
import Divider from '@mui/material/Divider';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { ReactComponent as LogoIcon } from 'assets/images/logo.svg';
import {
    BookmarkOutlineIcon,
    CampaignsIcon,
    ContactsIcon,
    CouponsIcon,
    HomeIcon,
    LocationsIcon,
    LogoutIcon,
    MessagingIcon,
    MicrositesIcon,
    QrCodesIcon,
    ReviewsIcon,
    SocialIcon,
    SurveysIcon,
} from 'assets/images/sidebar';
import BurgerButton from './BurgerButton';
import MenuItem from './MenuItem';
import CollapsedMenuItem from './CollapsedMenuItem';
import SidebarFooter from './SidebarFooter';
import useAccountSwitcher from './useAccountSwitcher';
import useProfileMenuItems from './useProfileMenuItems';
import { useSidebar } from './SidebarContext';
import useNavigationBookmarks from './useNavigationBookmarks';
import { isNavigationItemActive } from './NavigationLink';
import {
    Header,
    HeaderActions,
    List,
    ListItemButton,
    LogoLink,
    PluspointText,
    MobileTitle,
    SidebarDrawer,
    CollapseToggle,
} from './styles';

const BOOKMARKS_MENU_ID = 'pl-menu-left-link-bookmarks';

const Sidebar = memo(({ isMobile }) => {
    const [isExpanded, setExpanded] = useState(false);
    const [selectedBookmark, setSelectedBookmark] = useState(null);
    const [openMenu, setOpenMenu] = useState(null);
    const [logoutIsOpen, setLogoutIsOpen] = useState(false);

    const user = useSelector(state => state.auth.user);
    const account = useSelector(state => state.account?.account);
    const { isCollapsed, toggleCollapsed } = useSidebar();
    const { pathname } = useLocation();
    const { hasAccess, hasFullAccess } = usePermissions();
    const visibilityStatus = useVisibilityStatus();
    const bookmarkProps = useNavigationBookmarks();
    const collapsedMenuProps = { ...bookmarkProps, openMenu, setOpenMenu };
    const profileItems = useProfileMenuItems();
    const impersonation = useSelector(state => state.auth.impersonation);
    const isReady = !!user && !!account;
    const accountSwitcher = useAccountSwitcher(isReady && !impersonation);

    const { t } = useTranslation();

    const productsMaxNumberExceeded =
        account?.companiesMaxCount < account?.companies?.length || account?.usersMaxCount < account?.users?.length;
    const billingExpired = account && account?.billingDiffInSeconds < 0;

    const collapsed = !isMobile && isCollapsed;
    const isBillingRestricted = productsMaxNumberExceeded || billingExpired;
    const isQrOnlyPlan = account?.surveysEnabled === false;

    const MENU_ITEMS = [
        {
            id: 'pl-menu-left-link-home',
            title: t('Sidebar.home'),
            icon: <HomeIcon />,
            to: RoutePath.Home,
        },
        {
            id: 'pl-menu-left-link-contacts',
            title: t('Sidebar.contacts'),
            icon: <ContactsIcon />,
            module: 'contacts',
            to: RoutePath.Contacts,
        },
        {
            id: 'pl-menu-left-link-locations',
            title: t('Sidebar.locations'),
            icon: <LocationsIcon />,
            module: 'locations',
            alwaysEnabled: true,
            ...(isBillingRestricted
                ? { to: RoutePath.LocationsManage }
                : {
                      base: RoutePath.Locations,
                      children: [
                          { title: t('Sidebar.locationsManage'), to: RoutePath.LocationsManage },
                          { title: t('Sidebar.locationsProfiles'), to: RoutePath.LocationsProfiles },
                          // Fail open on a status error so an outage doesn't hide the section.
                          ...(visibilityStatus === true || visibilityStatus === 'error'
                              ? [
                                    {
                                        title: t('Sidebar.visibilityScanner'),
                                        to: RoutePath.LocationsVisibilityScanner,
                                    },
                                ]
                              : []),
                          { title: t('Sidebar.locationsMedia'), to: RoutePath.LocationsMedia },
                          { title: t('Sidebar.analytics'), to: RoutePath.LocationsAnalytics },
                      ],
                  }),
        },
        {
            id: 'pl-menu-left-link-reviews',
            title: t('Sidebar.reviews'),
            icon: <ReviewsIcon />,
            module: 'reviews',
            base: RoutePath.Reviews,
            children: [
                { title: t('Sidebar.reviewsManage'), to: RoutePath.ReviewsManage },
                {
                    title: t('Sidebar.reviewsAutomation'),
                    to: RoutePath.ReviewsAutomationAutoReplies,
                    base: RoutePath.ReviewsAutomation,
                },
                { title: t('Sidebar.reviewsWidgets'), to: RoutePath.ReviewsWidgets },
                {
                    title: t('Sidebar.analytics'),
                    to: RoutePath.ReviewsAnalyticsReviews,
                    base: RoutePath.ReviewsAnalytics,
                },
            ],
        },
        {
            id: 'pl-menu-left-link-social-posts',
            title: t('Sidebar.social'),
            icon: <SocialIcon />,
            module: 'posts',
            base: RoutePath.Social,
            children: [
                { title: t('Sidebar.socialPosts'), to: RoutePath.SocialPosts },
                { title: t('Sidebar.analytics'), to: RoutePath.SocialAnalytics },
            ],
        },
        {
            id: 'pl-menu-left-link-messaging',
            title: t('Sidebar.messaging'),
            icon: <MessagingIcon />,
            module: 'messaging',
            base: RoutePath.Messaging,
            children: [
                { title: t('Sidebar.messagingInbox'), to: RoutePath.MessagingInbox },
                ...(hasFullAccess('messaging')
                    ? [
                          {
                              title: t('Sidebar.pulseAI'),
                              to: RoutePath.MessagingPulseAIAgents,
                              base: RoutePath.MessagingPulseAI,
                          },
                      ]
                    : []),
                { title: t('Sidebar.analytics'), to: RoutePath.MessagingAnalytics },
            ],
        },
        {
            id: 'pl-menu-left-link-web-pages',
            title: t('Sidebar.webPages'),
            icon: <MicrositesIcon />,
            module: 'microsites',
            base: RoutePath.WebPages,
            children: [
                { title: t('Sidebar.microsites'), to: RoutePath.Microsites },
                ...(MENU_INDUSTRIES.includes(account?.industry)
                    ? [{ title: t('Sidebar.menus'), to: RoutePath.Menus }]
                    : []),
                { title: t('Sidebar.forms'), to: RoutePath.Forms },
            ],
        },
        {
            id: 'pl-menu-left-link-feedback',
            title: t(isQrOnlyPlan ? 'Sidebar.surveysQrOnly' : 'Sidebar.surveys'),
            icon: isQrOnlyPlan ? <QrCodesIcon /> : <SurveysIcon />,
            module: 'surveys',
            ...(isQrOnlyPlan
                ? {
                      to: RoutePath.Surveys,
                  }
                : {
                      base: RoutePath.Surveys,
                      children: [
                          {
                              title: t('Sidebar.responses'),
                              to: RoutePath.SurveysResponses,
                          },
                          {
                              title: t('Sidebar.templates'),
                              to: RoutePath.SurveysTemplates,
                          },
                          {
                              title: t('Sidebar.analytics'),
                              to: RoutePath.SurveysAnalyticsConversion,
                              base: RoutePath.SurveysAnalytics,
                          },
                      ],
                  }),
        },
        {
            id: 'pl-menu-left-link-campaigns',
            title: t('Sidebar.campaigns'),
            icon: <CampaignsIcon />,
            module: 'campaigns',
            ...(!account?.campaignsEnabled
                ? {
                      to: RoutePath.Campaigns,
                      upgradeRequired: true,
                  }
                : {
                      base: RoutePath.Campaigns,
                      children: [
                          {
                              title: t('Sidebar.campaignsOneTime'),
                              to: RoutePath.CampaignsOneTime,
                          },
                          {
                              title: t('Sidebar.campaignsAutomated'),
                              to: RoutePath.CampaignsAutomated,
                          },
                          {
                              title: t('Sidebar.campaignsLibrary'),
                              to: RoutePath.CampaignsLibrary,
                          },
                      ],
                  }),
        },
        {
            id: 'pl-menu-left-link-coupons',
            title: t('Sidebar.coupons'),
            icon: <CouponsIcon />,
            module: 'coupons',
            to: RoutePath.Coupons,
            upgradeRequired: !account?.couponsEnabled,
        },
    ].filter(item => !item.module || hasAccess(item.module));

    const bookmarkableItems = MENU_ITEMS.flatMap(item => {
        const disabled = !item.alwaysEnabled && isBillingRestricted;
        return item.children?.length
            ? item.children.map(child => ({ ...child, parentTitle: item.title, disabled }))
            : [{ ...item, id: undefined, disabled }];
    });
    bookmarkableItems.push(...profileItems.filter(item => !item.external).map(item => ({ ...item, id: undefined })));
    const titleCounts = bookmarkableItems.reduce((counts, item) => {
        counts.set(item.title, (counts.get(item.title) || 0) + 1);
        return counts;
    }, new Map());
    const bookmarkedItems = bookmarkProps.bookmarks
        .map(path => bookmarkableItems.find(item => item.to === path))
        .filter(Boolean)
        .map(item => ({
            ...item,
            title:
                item.parentTitle && titleCounts.get(item.title) > 1
                    ? `${item.title} (${item.parentTitle})`
                    : item.title,
        }));
    const activeBookmark = bookmarkedItems.find(item => item.to === selectedBookmark);
    const bookmarksSelected = !!activeBookmark && isNavigationItemActive(activeBookmark, pathname);

    useEffect(() => {
        if (!bookmarksSelected) setSelectedBookmark(null);
    }, [bookmarksSelected]);

    useEffect(() => {
        setOpenMenu(null);
    }, [pathname]);

    const handleNavigation = (item, fromBookmarks = false) => {
        if (!item.external) setSelectedBookmark(fromBookmarks ? item.to : null);
        setExpanded(false);
    };

    const navigationItems = [...MENU_ITEMS];
    if (bookmarkedItems.length) {
        navigationItems.splice(1, 0, {
            id: BOOKMARKS_MENU_ID,
            title: t('Sidebar.bookmarks'),
            icon: <BookmarkOutlineIcon className="sidebar-bookmark-icon sidebar-bookmark-outline" aria-hidden="true" />,
            alwaysEnabled: true,
            children: bookmarkedItems,
        });
    }

    const mobileTitleKey = getHeaderTitleKey(pathname, account);

    return (
        <SidebarDrawer
            slotProps={{ paper: { id: 'pl-menu-left-list' } }}
            variant="permanent"
            anchor={isMobile ? 'top' : 'left'}
            open
            isExpanded={isExpanded}
            isCollapsed={collapsed}
        >
            <Header isCollapsed={collapsed} onClick={collapsed ? toggleCollapsed : undefined}>
                {isMobile ? (
                    <>
                        <BurgerButton isExpanded={isExpanded} setExpanded={setExpanded} />
                        <MobileTitle variant="h6" noWrap>
                            {mobileTitleKey ? t(mobileTitleKey) : null}
                        </MobileTitle>
                        <Notifications />
                    </>
                ) : collapsed ? (
                    <>
                        <LogoIcon className="sidebar-logo" />
                        <CollapseToggle className="sidebar-expand" aria-label={t('Sidebar.expand')}>
                            <KeyboardDoubleArrowRightIcon sx={{ fontSize: 20 }} />
                        </CollapseToggle>
                    </>
                ) : (
                    <>
                        <LogoLink to={RoutePath.Home}>
                            <LogoIcon />
                            <PluspointText>Pluspoint</PluspointText>
                        </LogoLink>
                        <HeaderActions>
                            <CollapseToggle onClick={toggleCollapsed} aria-label={t('Sidebar.collapse')}>
                                <KeyboardDoubleArrowLeftIcon sx={{ fontSize: 20 }} />
                            </CollapseToggle>
                        </HeaderActions>
                    </>
                )}
            </Header>
            <List isExpanded={isExpanded} isMobile={isMobile} isCollapsed={collapsed}>
                {navigationItems.map(link => {
                    const Item = collapsed ? CollapsedMenuItem : MenuItem;
                    return (
                        <Item
                            key={link.id}
                            item={link}
                            activeBranch={bookmarksSelected === (link.id === BOOKMARKS_MENU_ID)}
                            callback={item => handleNavigation(item, link.id === BOOKMARKS_MENU_ID)}
                            disabled={link.alwaysEnabled ? false : isBillingRestricted}
                            {...(collapsed ? collapsedMenuProps : bookmarkProps)}
                        />
                    );
                })}
                {isMobile && (
                    <>
                        <Divider sx={{ backgroundColor: '#373737', margin: '16px 0' }} />
                        {profileItems.map(item =>
                            item.external ? (
                                <ExternalLink
                                    key={item.id}
                                    href={item.href}
                                    sx={{ textDecoration: 'none', width: '100%' }}
                                >
                                    <ListItemButton onClick={() => setExpanded(false)}>
                                        <span className="sidebar-active-indicator" />
                                        {item.icon}
                                        <Typography variant="body1">{item.title}</Typography>
                                    </ListItemButton>
                                </ExternalLink>
                            ) : (
                                <MenuItem
                                    key={item.id}
                                    item={item}
                                    activeBranch={!bookmarksSelected}
                                    callback={handleNavigation}
                                    {...bookmarkProps}
                                />
                            ),
                        )}
                        <ListItemButton
                            onClick={() => {
                                setExpanded(false);
                                setLogoutIsOpen(true);
                            }}
                        >
                            <span className="sidebar-active-indicator" />
                            <LogoutIcon />
                            <Typography variant="body1">{t('Sidebar.logout')}</Typography>
                        </ListItemButton>
                    </>
                )}
            </List>
            {!isMobile && isReady && (
                <SidebarFooter
                    collapsed={collapsed}
                    activeBranch={!bookmarksSelected}
                    items={profileItems}
                    accountSwitcher={accountSwitcher}
                    onLogout={() => setLogoutIsOpen(true)}
                    openMenu={openMenu}
                    setOpenMenu={setOpenMenu}
                />
            )}
            <LogoutModal isOpen={logoutIsOpen} setIsOpen={setLogoutIsOpen} />
        </SidebarDrawer>
    );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
